import { type Cohort, DataTypeXceed } from '@efdigital/ll-addon-data';
import { findRecord, query } from '@warp-drive/utilities/json-api';

import { include, jsonApiOptions, page } from '../../json-api.ts';

/** Splits a large array into smaller arrays of a given size. */
function chunkArray(array: string[], size: number): string[][] {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

/**
 * The query to get cohorts by participant group IDs.
 * @param participantGroupIds - The IDs of the participant groups to query for.
 * @returns An array of query requests for the cohorts.
 */
export function queryCohortsByParticipantGroupIds(
  participantGroupIds: string[]
) {
  const PAGE_SIZE = 100;
  const chunks = chunkArray(participantGroupIds, PAGE_SIZE).filter(
    (chunk) => chunk.length > 0
  );
  return chunks.map((chunk) =>
    query<Cohort>(
      DataTypeXceed.Cohort,
      {
        'filter[group.id][$in][]': chunk,
        ...page({ offset: 0, limit: chunk.length }),
        // Sideload the coaching relationship: coachee detection reads
        // `cohort.coaching?.id`, and without the include that lazily fetches
        // the relationship's `related` link — which is malformed server-side
        // (produces a 404) and breaks the coaching pages.
        ...include(['coaching', 'coaching.coachingConfig', 'program']),
      },
      jsonApiOptions
    )
  );
}

/**
 * The query to get a cohort by ID.
 * @param cohortId - The ID of the cohort to query for.
 * @returns A query request for the cohort.
 */
export function findCohortById(cohortId: string) {
  return findRecord<Cohort>(DataTypeXceed.Cohort, cohortId, {
    include: [
      'coaching',
      'program',
      'pages',
      'modules',
      'quizzes',
      'certificates',
      'goals',
      'contacts',
      'coaching.coachingConfig',
    ],
    ...jsonApiOptions,
  });
}
